import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Outlet, createRootRouteWithContext, useRouter } from "@tanstack/react-router";
import { AppSidebar } from "@/components/AppSidebar";
import { TopBar } from "@/components/TopBar";
import { CommandPalette } from "@/components/CommandPalette";
import { SideRail } from "@/components/SideRail";
import { CompanionProvider } from "@/state/CompanionContext";
import { SideRailProvider } from "@/state/SideRail";
import { ActionStoreProvider } from "@/state/ActionStore";
import { useGlobalShortcuts } from "@/hooks/useGlobalShortcuts";

function NotFoundComponent() {
  return (
    <div className="bg-canvas flex min-h-screen items-center justify-center">
      <div className="text-muted-foreground text-[13px]">404 — page not found</div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  return (
    <div className="bg-canvas flex min-h-screen items-center justify-center">
      <div className="text-center">
        <div className="text-foreground text-[13px]">Something went wrong</div>
        <button
          onClick={() => {
            router.invalidate();
            reset();
          }}
          className="border-border bg-background hover:bg-muted mt-3 rounded-sm border px-3 py-1 text-[12px]"
        >
          Retry
        </button>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <ActionStoreProvider>
        <CompanionProvider>
          <SideRailProvider>
            <ShortcutsAndChrome />
          </SideRailProvider>
        </CompanionProvider>
      </ActionStoreProvider>
    </QueryClientProvider>
  );
}

function ShortcutsAndChrome() {
  useGlobalShortcuts();
  return (
    <div className="bg-canvas flex h-screen w-full overflow-hidden">
      <AppSidebar />
      <div className="relative flex min-w-0 flex-1 flex-col">
        <TopBar />
        <main className="min-h-0 flex-1 overflow-hidden">
          <Outlet />
        </main>
        <SideRail />
      </div>
      <CommandPalette />
    </div>
  );
}
